import { useEffect, useState } from 'react';
import { useTranslate, useUpdate } from '@refinedev/core';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Modal,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import type { PublicationDetailOut } from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';
import {
  isOutsideAvailability,
  pickerValueToRange,
  rangeToPickerValue,
  windowProblem,
} from './exclusivityWindow';
import type { PickerValue } from './exclusivityWindow';
import {
  buildExclusiveUntilPatch,
  exclusiveUntilToPickerValue,
  formatExclusiveTargets,
  isDisabledExclusiveDay,
  isPastValue,
  productDateFromWindow,
} from './formatExclusiveUntil';
import type { FormatExclusiveValues } from './formatExclusiveUntil';

const { Text } = Typography;

const EMPTY_VALUES: FormatExclusiveValues = { ebook: null, audio: null };

interface ExclusivityModalProps {
  record: PublicationDetailOut;
  open: boolean;
  onClose: () => void;
}

/**
 * The publication's exclusivity window and its products' end dates, set
 * together — one PATCH carries both.
 *
 * The two fields do not share semantics: the window may sit in the past and
 * may be cleared, while a product date may do neither. That is why the
 * "apply to products" shortcut can be unavailable while the window itself
 * still saves.
 */
export const ExclusivityModal = ({
  record,
  open,
  onClose,
}: ExclusivityModalProps) => {
  const t = useTranslate();
  const [value, setValue] = useState<PickerValue>(null);
  const [values, setValues] = useState<FormatExclusiveValues>(EMPTY_VALUES);
  const [applyToProducts, setApplyToProducts] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { mutate: updatePublication, mutation } =
    useUpdate<PublicationDetailOut>();
  const isPending = mutation.isPending;

  const T = 'user_catalog.publications.show.exclusivity';

  const targets = formatExclusiveTargets(record, (key) =>
    t(`user_catalog.publications.tabs.${key}`)
  );

  useEffect(() => {
    if (!open) return;
    setValue(rangeToPickerValue(record.exclusive_ranges));
    setValues({
      ebook: exclusiveUntilToPickerValue(record.ebook?.exclusive_until),
      audio: exclusiveUntilToPickerValue(record.audio?.exclusive_until),
    });
    setApplyToProducts(false);
    setSaveError(null);
  }, [
    open,
    record.exclusive_ranges,
    record.ebook?.exclusive_until,
    record.audio?.exclusive_until,
  ]);

  const problem = windowProblem(value);
  const range = pickerValueToRange(value);
  const outside =
    !problem && isOutsideAvailability(range, record.available_ranges);

  // Null means the window's end cannot become a product date — open-ended, or
  // already past. The checkbox reports which, rather than failing on save.
  const carried = productDateFromWindow(value);
  const carryReason = !value?.[1]
    ? `${T}.carry_open_ended`
    : !carried
      ? `${T}.carry_past`
      : null;
  const canCarry = !carryReason;
  const effective: FormatExclusiveValues =
    applyToProducts && carried
      ? (Object.fromEntries(
          targets.map((target) => [target.key, carried])
        ) as FormatExclusiveValues)
      : values;

  // The window is sent whenever it differs; product dates go through the
  // builder, which drops anything empty or unchanged.
  const formatPatch = buildExclusiveUntilPatch(targets, effective);

  // The picked values survive a failed save — the operator keeps their input
  // and the stored values are left untouched.
  const save = (
    windowRange: ReturnType<typeof pickerValueToRange>,
    outcome: 'saved' | 'cleared'
  ) => {
    setSaveError(null);
    updatePublication(
      {
        resource: 'user_catalog.publications',
        id: record.id,
        values: {
          exclusive_ranges: windowRange,
          ...(outcome === 'saved' ? formatPatch : {}),
        },
        meta: { method: 'patch' },
        // Refine's defaults name the raw resource; say what happened instead.
        successNotification: {
          type: 'success',
          message: t('notifications.success'),
          description: t(`${T}.${outcome}`),
        },
        errorNotification: false,
      },
      {
        onSuccess: onClose,
        onError: (error) =>
          setSaveError(
            extractBackendMessage(error) ?? t(`${T}.${outcome}_failed`)
          ),
      }
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t(`${T}.modal_title`)}
      destroyOnClose
      footer={
        <Space>
          {record.exclusive_ranges && (
            <Popconfirm
              title={t(`${T}.clear_confirm_title`)}
              description={t(`${T}.clear_confirm_description`)}
              okText={t(`${T}.clear`)}
              okButtonProps={{ danger: true, loading: isPending }}
              cancelText={t('actions.cancel')}
              onConfirm={() => save(null, 'cleared')}
            >
              <Button danger loading={isPending}>
                {t(`${T}.clear`)}
              </Button>
            </Popconfirm>
          )}
          <Button onClick={onClose}>{t('actions.cancel')}</Button>
          <Button
            type="primary"
            loading={isPending}
            disabled={!!problem}
            onClick={() => save(range, 'saved')}
          >
            {t('actions.save')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">{t(`${T}.hint`)}</Text>
        <DatePicker.RangePicker
          value={value}
          onChange={(next) => setValue(next as PickerValue)}
          allowEmpty={[false, true]}
          // An empty end is a real value here, not a missing one.
          placeholder={[
            t('common.range.from'),
            t('user_catalog.publications.show.windows.open_ended'),
          ]}
          format="DD.MM.YYYY"
          status={problem === 'inverted' ? 'error' : undefined}
          disabled={isPending}
          style={{ width: '100%' }}
        />
        {problem === 'inverted' && (
          <Text type="danger">{t(`${T}.invalid_order`)}</Text>
        )}
        {outside && (
          <Alert type="warning" showIcon message={t(`${T}.outside_warning`)} />
        )}

        {targets.length > 0 && (
          <>
            <Divider style={{ margin: 0 }} />
            <Tooltip title={carryReason ? t(carryReason) : undefined}>
              {/* A disabled checkbox swallows hover, so the tooltip needs a
                  wrapper of its own to stay reachable. */}
              <span style={{ display: 'inline-block' }}>
                <Checkbox
                  checked={applyToProducts}
                  disabled={!canCarry || isPending}
                  onChange={(event) => setApplyToProducts(event.target.checked)}
                >
                  {t(`${T}.carry_to_products`)}
                </Checkbox>
              </span>
            </Tooltip>
            <Text type="secondary">{t(`${T}.formats_hint`)}</Text>
            {targets.map((target) => {
              const fieldValue = effective[target.key];
              return (
                <Space
                  key={target.key}
                  direction="vertical"
                  size={4}
                  style={{ width: '100%' }}
                >
                  <Text strong>{target.label}</Text>
                  <DatePicker
                    value={fieldValue}
                    onChange={(next) =>
                      setValues((current) => ({
                        ...current,
                        [target.key]: next,
                      }))
                    }
                    format="DD.MM.YYYY"
                    disabledDate={(day) => isDisabledExclusiveDay(day)}
                    // A stored date cannot be cleared — the backend rejects null.
                    allowClear={!target.current}
                    placeholder={t(`${T}.placeholder`)}
                    disabled={isPending || applyToProducts}
                    style={{ width: '100%' }}
                  />
                  {isPastValue(fieldValue) && (
                    <Text type="warning">{t(`${T}.past_warning`)}</Text>
                  )}
                </Space>
              );
            })}
          </>
        )}

        {saveError && <Alert type="error" showIcon message={saveError} />}
      </Space>
    </Modal>
  );
};
